import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const laColleSurLoupEn: ChauffeurPage = {
  slug: "la-colle-sur-loup",
  title: "VTC & private chauffeur in La Colle-sur-Loup",
  subtitle:
    "Private chauffeur in La Colle-sur-Loup: the village of roses and antique dealers in the Loup valley, next to Saint-Paul-de-Vence.",
  keywords: [
    "vtc la colle-sur-loup",
    "private chauffeur la colle-sur-loup",
    "la colle sur loup airport transfer",
    "saint-paul-de-vence chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in La Colle-sur-Loup",
    subtitle:
      "The village of roses and antique dealers in the Loup valley, at the gates of Saint-Paul-de-Vence: Nice airport transfers and hourly hire.",
    keywords: [
      "VTC La Colle-sur-Loup",
      "Loup valley chauffeur",
      "Nice airport transfer",
      "Saint-Paul-de-Vence chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in La Colle-sur-Loup",
    badges: ["Loup valley", "Next to Saint-Paul", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in La Colle-sur-Loup",
    items: [
      {
        title: "At the gates of Saint-Paul-de-Vence",
        description:
          "La Colle adjoins the art village of Saint-Paul: the chauffeur links the two in a few minutes and avoids the search for parking below the ramparts.",
      },
      {
        title: "Village of roses & antique dealers",
        description:
          "A land of rose gardens and renowned antique dealers: a leisurely day along the valley, with a dedicated chauffeur who waits between shops.",
      },
      {
        title: "Nice airport ~35 min",
        description:
          "Reach Nice Côte d’Azur airport in about 35 minutes via Cagnes-sur-Mer and the A8, with flight tracking.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "FR/EN, based on the French Riviera, punctual and discreet — 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in La Colle-sur-Loup",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up in La Colle-sur-Loup with flight tracking, to and from Nice Côte d’Azur airport, in about 35 minutes.",
      },
      {
        title: "Saint-Paul-de-Vence shuttle",
        text: "A link to Saint-Paul-de-Vence and the Maeght Foundation, drop-off as close as possible to the ramparts, pick-up on request.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or the day: antique dealers, rose gardens, restaurants of the Loup valley and the neighbouring villages.",
      },
      {
        title: "Loup valley outing",
        text: "A route towards Villeneuve-Loubet, Vence and the back country, with a chauffeur who knows the area’s roads.",
      },
      {
        title: "Long distance",
        text: "Journeys of any distance from La Colle-sur-Loup: Cannes, Nice, Monaco, Italy and ski resorts, in a comfortable vehicle.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Old village of La Colle",
        description: "Historic heart, Place de la Libération and terraces.",
      },
      {
        name: "Antique dealers & brocantes",
        description: "Renowned shops along the road to Saint-Paul.",
      },
      {
        name: "Loup valley",
        description:
          "River, rose gardens and countryside at the village gates.",
      },
      {
        name: "Saint-Paul-de-Vence",
        description:
          "Neighbouring art village, Maeght Foundation and ramparts.",
      },
      {
        name: "Villeneuve-Loubet",
        description: "Towards the sea and Marina Baie des Anges.",
      },
      {
        name: "Nice airport",
        description: "About 35 minutes via Cagnes-sur-Mer and the A8.",
      },
    ],
  },
  generic: {
    heading: "La Colle-sur-Loup, roses and antique dealers of the Loup",
    subheading:
      "A village of the Loup valley, next to Saint-Paul-de-Vence, between rose gardens and antique shops.",
    paragraphs: [
      "La Colle-sur-Loup cultivates a valley art of living: a land of roses and antique dealers, its shops line the road to Saint-Paul and its old village invites you to wander. The chauffeur drops you in the heart of the village or in front of the antique dealers and waits while you make your finds.",
      "An immediate neighbour of Saint-Paul-de-Vence, La Colle is the ideal base to reach the art village, the Maeght Foundation, Vence or Villeneuve-Loubet, with no worry about parking below the ramparts. Our chauffeurs link these sites in a few minutes.",
      "For Nice Côte d’Azur airport, allow around 35 minutes via Cagnes-sur-Mer and the A8 depending on traffic; our bilingual chauffeurs track your flight and adjust the departure.",
    ],
    bullets: [
      "Saint-Paul-de-Vence link",
      "Antique dealers & rose gardens",
      "Flight tracking & instant quote",
    ],
  },
  process: {
    heading: "Book a chauffeur in La Colle-sur-Loup",
    steps: [
      "Share your route (village, Saint-Paul, Nice airport) and your times.",
      "We confirm the right vehicle and a fixed fare or hourly hire.",
      "Your chauffeur tracks your flight and greets you at the agreed address.",
      "Live adjustments to suit traffic and your programme.",
    ],
  },
  faq: [
    cityPriceFaq("en", "La Colle-sur-Loup"),
    {
      q: "How long is it to Nice airport?",
      a: "Allow about 35 minutes via Cagnes-sur-Mer and the A8 depending on traffic, with flight tracking and a personal welcome.",
    },
    {
      q: "Do you handle the link to Saint-Paul-de-Vence?",
      a: "Yes, La Colle adjoins Saint-Paul-de-Vence: we drop you as close as possible to the ramparts and the Maeght Foundation, with pick-up on request and no parking worry.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during the Saint-Paul events; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes E-Class and S-Class sedans, 7-seater van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Can I tour the antique dealers with a chauffeur?",
      a: "Yes, with hourly or daily hire the chauffeur drives you from one shop to the next along the Loup valley and waits between visits.",
    },
  ],
  relatedLinks: [
    {
      label: "French Riviera rates and routes",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Meet and greet at Nice Airport", href: "/en/chauffeur/airport" },
    { label: "Private chauffeur in Nice", href: "/en/chauffeur/nice" },
    { label: "Rent a car in Vence", href: "/en/location/car-rental-vence" },
  ],
  cta: {
    title: "Your private chauffeur in La Colle-sur-Loup",
    text: "Nice airport transfer, Saint-Paul shuttle or hourly hire — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service La Colle-sur-Loup — private chauffeur",
    description:
      "Private car service in La Colle-sur-Loup: Nice airport transfer in ~35 min, roses, antique dealers and Saint-Paul-de-Vence shuttle. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/la-colle-sur-loup",
  },
};

export default laColleSurLoupEn;
